import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api.js';
import { useAuth } from '../../auth/AuthProvider.js';

interface ISNode {
  id: string;
  code: string;
  name: string;
  type: string;
  parent_id: string | null;
  balance: string;
  children: ISNode[];
}

interface ISResponse {
  from: string;
  to: string;
  revenue: ISNode[];
  expense: ISNode[];
  totals: { revenue: string; expense: string; net_income: string };
}

interface RowProps {
  node: ISNode;
  depth: number;
  // For revenue rows we want to flip the sign so positive numbers show.
  flipSign: boolean;
}

// Recursively sum a node's own balance plus all its descendants. Parents in
// the seed CoA carry balance=0 and only their leaf children have movement,
// so without rollup the user sees zeros at every collapsed level.
function rollupBalance(node: ISNode): bigint {
  let sum = BigInt(node.balance);
  for (const child of node.children) sum += rollupBalance(child);
  return sum;
}

function ReportRow({ node, depth, flipSign }: RowProps) {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = node.children.length > 0;
  const raw = hasChildren ? rollupBalance(node) : BigInt(node.balance);
  const display = flipSign ? -raw : raw;
  const isZero = display === 0n;

  return (
    <>
      <tr className="border-b text-sm hover:bg-slate-50">
        <td
          className="py-1 font-mono text-xs text-slate-500"
          style={{ paddingLeft: `${depth * 16 + 8}px` }}
        >
          {hasChildren && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mr-1 text-slate-400 hover:text-slate-600"
            >
              {expanded ? '▾' : '▸'}
            </button>
          )}
          {node.code}
        </td>
        <td className="px-2 py-1" style={{ paddingLeft: `${depth * 16 + 8}px` }}>
          {node.name}
        </td>
        <td className={`px-2 py-1 text-right font-mono text-sm ${isZero ? 'text-slate-300' : ''}`}>
          {display.toString()}
        </td>
      </tr>
      {expanded &&
        hasChildren &&
        node.children.map((child) => (
          <ReportRow key={child.id} node={child} depth={depth + 1} flipSign={flipSign} />
        ))}
    </>
  );
}

export function IncomeStatement() {
  const { t } = useTranslation('bookkeeping');
  const { activeOrgId } = useAuth();
  const today = new Date().toISOString().slice(0, 10);
  const firstDayThisMonth = `${today.slice(0, 7)}-01`;
  const [from, setFrom] = useState(firstDayThisMonth);
  const [to, setTo] = useState(today);

  const { data, isLoading, error } = useQuery({
    queryKey: ['income-statement', activeOrgId, from, to],
    queryFn: () => api<ISResponse>(`/bookkeeping/reports/income-statement?from=${from}&to=${to}`),
    enabled: !!activeOrgId && !!from && !!to,
  });

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">{t('incomeStatement.title')}</h1>

      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm text-slate-600">
          {t('incomeStatement.from')}:
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="ml-2 rounded border px-2 py-1 text-sm"
          />
        </label>
        <label className="text-sm text-slate-600">
          {t('incomeStatement.to')}:
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="ml-2 rounded border px-2 py-1 text-sm"
          />
        </label>
      </div>

      {isLoading && <p className="text-sm text-slate-500">{t('common:loading')}</p>}
      {error && (
        <p className="text-sm text-red-600">{(error as { message?: string }).message ?? 'Error'}</p>
      )}

      {data && (
        <div className="space-y-4">
          <ReportSection
            title={t('incomeStatement.revenue')}
            nodes={data.revenue}
            flipSign={true}
            total={data.totals.revenue}
            totalLabel={t('incomeStatement.total_revenue')}
          />
          <ReportSection
            title={t('incomeStatement.expense')}
            nodes={data.expense}
            flipSign={false}
            total={data.totals.expense}
            totalLabel={t('incomeStatement.total_expense')}
          />
          <NetIncome value={data.totals.net_income} t={t} />
        </div>
      )}
    </div>
  );
}

function ReportSection({
  title,
  nodes,
  flipSign,
  total,
  totalLabel,
}: {
  title: string;
  nodes: ISNode[];
  flipSign: boolean;
  total: string;
  totalLabel: string;
}) {
  return (
    <div className="overflow-x-auto rounded border bg-white">
      <h2 className="border-b bg-slate-50 px-3 py-2 text-sm font-semibold">{title}</h2>
      <table className="w-full">
        <tbody>
          {nodes.map((n) => (
            <ReportRow key={n.id} node={n} depth={0} flipSign={flipSign} />
          ))}
          <tr className="border-t bg-slate-50 text-sm font-semibold">
            <td className="px-2 py-2" colSpan={2}>
              {totalLabel}
            </td>
            <td className="px-2 py-2 text-right font-mono">{total}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function NetIncome({ value, t }: { value: string; t: (k: string) => string }) {
  const v = BigInt(value);
  const positive = v >= 0n;
  return (
    <div
      className={`rounded border px-4 py-3 text-base font-semibold ${
        positive
          ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
          : 'border-rose-300 bg-rose-50 text-rose-900'
      }`}
    >
      {t('incomeStatement.net_income')}: <span className="font-mono">{value}</span>
    </div>
  );
}
